import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { TextField } from "@mui/material";

import toast from "react-hot-toast";

import { useHttpClient } from "../../../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../../../shared/context/LoadingContext";

import { produce } from "immer";
import NotaInformativa from "../../../../../../../shared/components/notaInformativa/NotaInformativa";
import {
  parcelasComFte,
  mensagemParcelasComFte,
} from "../../../../../../../shared/util/parcelasComFte";

import "./ModalEditarDesconto.scss";
function ModalEditarDesconto({
  open,
  handleCloseModalEditarDesconto,
  modalData,
  updateContaCorrente,
  contaCorrente,
  auth,
}) {
  const isReembolso =
    modalData.orcamento.modalidadePagamento === "REEMBOLSO_CLIENTE";

  /* As parcelas já faturadas. Calculadas uma vez e usadas em dois sítios: o
     aviso no topo da janela e a recusa da isenção total. */
  const comFte = parcelasComFte(modalData.parcelas);

  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  const [caixaClinicaId, setCaixaClinicaId] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tipoDesconto, setTipoDesconto] = useState(
    modalData.orcamento.tipoDesconto,
  );
  const [desconto, setDesconto] = useState(
    modalData.orcamento.tipoDesconto === "$"
      ? modalData.orcamento.desconto
      : modalData.orcamento.percentagemDescontoOriginal,
  );
  const [descontoEmEscudos, setDescontoEmEscudos] = useState(
    modalData.orcamento.desconto,
  );

  const porReceber = modalData.parcelas.reduce(
    (accumulator, currentValue) =>
      currentValue.parcelaPaga
        ? accumulator
        : accumulator + currentValue.quantiaPorPagar,
    0,
  );

  const porReceberMaisDesconto = isReembolso
    ? modalData.orcamento.subtotalCliente
    : porReceber + modalData.orcamento.desconto;

  // Valores fixos que não mudam durante a edição
  const majoracao = modalData.orcamento.majoracao || 0;
  const comMajoracao = modalData.orcamento.comMajoracao || false;
  // Valor base para cálculos (subtotal após aplicar majoração)
  const valorBase = isReembolso
    ? modalData.orcamento.subtotalCliente
    : comMajoracao
      ? modalData.orcamento.subtotal - majoracao
      : modalData.orcamento.subtotal;

  // Função simplificada para calcular o total após desconto
  const calcularTotal = (descontoValue) => {
    let valorARetornar = 0;

    if (isReembolso)
      valorARetornar = modalData.orcamento.subtotal - descontoValue;
    else valorARetornar = valorBase - descontoValue;

    return valorARetornar;
  };

  useEffect(() => {
    const fetchCaixas = async () => {
      startLoading();
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${contaCorrente.clinica}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
        setCaixaClinicaId(
          responseData.caixas.find((cai) => cai.caixa === "Clínica")._id,
        );
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchCaixas();
  }, []);

  const submit = async () => {
    startLoading();
    try {
      /* ─── A ISENÇÃO TOTAL É A QUE MAIS DESTRÓI, E PASSAVA PRIMEIRO ────────
       *
       * A guarda da FTE estava LOGO A SEGUIR a este bloco, e os 100% saíam
       * daqui antes de lá chegarem. Quem escrevesse um desconto exatamente
       * igual ao subtotal passava-lhe por cima.
       *
       * E a isenção é o caminho que mais estraga: o `handleFullExemption`
       * manda uma parcela só — «Parcela de isenção» — para o
       * `substituirParcelasDaConta`, que APAGA todas as antigas. A parcela
       * com a FTE desaparecia, e a fatura ficava a apontar para um registo
       * que já não existe: sai sem nome no mapa de vendas, que é o mapa que
       * vai para a contabilidade.
       *
       * Foi assim que ficou uma FTE órfã na K Dent, a 28/08/2026.
       *
       * Aqui não há «total livre» que chegue: isentar põe a conta a zero, e
       * isso obriga a mexer em TODAS as parcelas, faturada incluída.
       *
       * O `==` é de propósito, e o lint queixa-se dele. Tem de ser EXATAMENTE
       * a mesma comparação da linha que delega para a isenção, logo abaixo:
       * com `===`, um dos dois valores a chegar como texto fazia a guarda não
       * disparar e a isenção disparar — e o buraco voltava, só que mais
       * difícil de ver. Se um dia se trocar um, trocam-se os dois. */
      // eslint-disable-next-line eqeqeq
      if (comFte.length > 0 && descontoEmEscudos == valorBase) {
        toast.error(
          mensagemParcelasComFte(comFte, "isentar esta conta por inteiro"),
        );
        setIsSubmitting(false);
        return;
      }

      // Caso o desconto seja igual ao subtotal (isenção total) → delega
      if (descontoEmEscudos == valorBase) {
        await handleFullExemption();
        return;
      }

      // Verifica se o desconto foi alterado
      if (descontoEmEscudos == modalData.orcamento.desconto) {
        toast.error("O desconto permanece o mesmo. Altere-o para continuar");
        setIsSubmitting(false);
        return;
      }

      // GUARDA FTE: um aumento de desconto consome parcelas não pagas, mas
      // parcelas com FTE emitida não podem ser eliminadas (consistência fiscal).
      // Se o valor a absorver exceder o total "livre" (não pago, sem FTE),
      // bloqueia antes de tocar no orçamento ou nas parcelas.
      if (descontoEmEscudos > modalData.orcamento.desconto) {
        const diferenca = descontoEmEscudos - modalData.orcamento.desconto;
        const totalLivre = modalData.parcelas
          .filter((p) => !p.parcelaPaga && !p.documentoEletronico)
          .reduce((s, p) => s + p.quantiaPorPagar, 0);

        if (diferenca > totalLivre + 0.01) {
          toast.error(
            "Existem parcelas com FTE emitida que impedem este desconto. " +
              "Anule a FTE ou ajuste o valor.",
          );
          setIsSubmitting(false);
          return;
        }
      }

      // 1. Calcular a conta atualizada localmente
      //    (mantém toda a lógica de handleDiscountIncrease/Decrease)
      const contaAtualizada = produce(modalData, (draft) => {
        if (descontoEmEscudos > modalData.orcamento.desconto) {
          handleDiscountIncrease(
            draft,
            descontoEmEscudos - modalData.orcamento.desconto,
          );
        } else {
          handleDiscountDecrease(
            draft,
            modalData.orcamento.desconto - descontoEmEscudos,
          );
        }

        draft.total = calcularTotal(descontoEmEscudos);

        const temParcelasNaoPagas = draft.parcelas.some((p) => !p.parcelaPaga);
        draft.contaLiquidada = !temParcelasNaoPagas;
      });

      // 2. Atualizar o orçamento (igual ao antes)
      await updateOrcamentoDiscount();

      // 3. Atualizar parcelas (preservando IDs existentes para não perder DFEs)
      const body = {
        contaCorrenteId: contaCorrente._id,
        contaId: modalData._id,
        total: contaAtualizada.total,
        contaLiquidada: contaAtualizada.contaLiquidada,
        parcelas: contaAtualizada.parcelas,
        utilizador: auth.userId,
        nomeCliente: contaCorrente.cliente.nome,
        operacao: "ATUALIZAR_DESCONTO",
      };

      const contaCorrenteParaState = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/atualizarParcelasDaConta/${modalData._id}`,
        "PATCH",
        JSON.stringify(body),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );

      toast.success(
        `Desconto atualizado com sucesso!${
          contaAtualizada.contaLiquidada ? " Conta Liquidada!" : ""
        }`,
      );

      updateContaCorrente(contaCorrenteParaState.contaCorrente);
    } catch (err) {
      console.error("err", err);
    } finally {
      handleCloseModalEditarDesconto();
      stopLoading();
    }
  };
  // Função para tratar isenção total
  const handleFullExemption = async () => {
    try {
      // 1. Atualizar o orçamento (igual ao antes)
      await updateOrcamentoDiscount(0);

      // 2. Substituir parcelas da conta — uma só parcela simbólica
      const body = {
        contaCorrenteId: contaCorrente._id,
        contaId: modalData._id,
        total: 0,
        contaLiquidada: true,
        nrParcelas: 1,
        orcamentoId: modalData.orcamento._id,
        parcelas: [
          {
            cancelamentosDocumentosEletronicos:
              modalData.parcelas[0].cancelamentosDocumentosEletronicos,
            dataLimitePagamento: new Date(),
            dataPagamento: new Date(),
            parcelaPaga: true,
            metodoPagamento: "Dinheiro",
            quantiaPaga: 0,
            quantiaPorPagar: 0,
            nrParcela: "1/1",
            observacao: "Parcela de isenção",
            caixa: caixaClinicaId,
          },
        ],
        utilizador: auth.userId,
        nomeCliente: contaCorrente.cliente.nome,
        operacao: "ISENCAO_TOTAL_DESCONTO",
      };

      const contaCorrenteParaState = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/substituirParcelasDaConta/${modalData._id}`,
        "PATCH",
        JSON.stringify(body),
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );

      toast.success(
        "Desconto atualizado com sucesso! Conta Fechada e isenta de pagamento.",
      );
      updateContaCorrente(contaCorrenteParaState.contaCorrente);
    } catch (err) {
      console.error("err", err);
    }
  };

  // Função para atualizar o orçamento com o novo desconto
  const updateOrcamentoDiscount = async (totalOverride = null) => {
    const formData = new FormData();
    formData.append("desconto", Number(descontoEmEscudos));
    formData.append("tipoDesconto", tipoDesconto);
    if (tipoDesconto === "%") {
      formData.append("percentagemDescontoOriginal", desconto);
    }

    const novoTotal = totalOverride ?? calcularTotal(Number(descontoEmEscudos));

    formData.append("total", novoTotal);
    formData.append("utilizador", auth.userId);
    formData.append("subtotal", modalData.orcamento.subtotal);

    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/dadosdesconto/${modalData.orcamento._id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );
    } catch (err) {
      console.error("err", err);
    }
  };

  // Função para gerenciar aumento de desconto
  const handleDiscountIncrease = (draft, diferencaDesconto) => {
    // Parcelas com FTE emitida não podem ser consumidas/eliminadas
    // (só podem ser pagas exato/parcial). Excluímos da combinação.
    const parcelasNaoPagas = draft.parcelas.filter(
      (p) => !p.parcelaPaga && !p.documentoEletronico,
    );

    // Utiliza a lógica existente para encontrar combinações
    let combinacoesPossiveis = encontrarCombinacoesExatas(
      parcelasNaoPagas,
      diferencaDesconto,
    );

    // Se não houver combinação exata, busca a mais próxima
    if (combinacoesPossiveis.length === 0) {
      combinacoesPossiveis = encontrarMelhorCombinacao(
        parcelasNaoPagas,
        diferencaDesconto,
      );
    }

    if (combinacoesPossiveis.length > 0) {
      const parcelasParaDescontar = combinacoesPossiveis[0];
      let descontoRestante = diferencaDesconto;

      // Aplicando o desconto às parcelas selecionadas
      for (const parcela of parcelasParaDescontar) {
        const index = draft.parcelas.findIndex((p) => p._id === parcela._id);

        if (descontoRestante >= parcela.quantiaPorPagar) {
          // Remove a parcela completamente
          draft.parcelas.splice(index, 1);
          descontoRestante -= parcela.quantiaPorPagar;
        } else {
          // Reduz o valor da parcela
          draft.parcelas[index].quantiaPorPagar -= descontoRestante;
          descontoRestante = 0;
        }
      }

      // Aplica desconto restante à próxima parcela, se houver
      if (descontoRestante > 0) {
        const proximaParcelaNaoPaga = draft.parcelas.find(
          (p) => !p.parcelaPaga,
        );
        if (proximaParcelaNaoPaga) {
          const index = draft.parcelas.indexOf(proximaParcelaNaoPaga);
          if (descontoRestante >= proximaParcelaNaoPaga.quantiaPorPagar) {
            draft.parcelas.splice(index, 1);
          } else {
            draft.parcelas[index].quantiaPorPagar -= descontoRestante;
          }
        }
      }

      // Renumera as parcelas
      renumberParcelas(draft);
    }
  };

  // Função para gerenciar redução de desconto
  const handleDiscountDecrease = (draft, diferencaDesconto) => {
    // Remover a parcela simbólica de isenção (paga com 0 escudos e 0 por pagar).
    // Ao reverter uma isenção total, se esta parcela ficasse, o resultado seria
    // uma parcela paga de 0$ + a nova parcela com o valor restaurado.
    // Só a parcela de isenção tem parcelaPaga=true com quantiaPaga=0, por isso
    // este filtro não afeta parcelas realmente pagas.
    draft.parcelas = draft.parcelas.filter(
      (p) =>
        !(
          p.parcelaPaga === true &&
          (p.quantiaPaga || 0) === 0 &&
          (p.quantiaPorPagar || 0) === 0
        ),
    );

    // Cria uma nova parcela com o valor da diferença
    const novaParcela = {
      quantiaPaga: 0,
      quantiaPorPagar: diferencaDesconto,
      dataLimitePagamento: new Date(),
      parcelaPaga: false,
      cancelamentosDocumentosEletronicos: [],
    };

    // Adiciona a nova parcela
    draft.parcelas.push(novaParcela);
    draft.nrParcelas = draft.parcelas.length;
    draft.contaLiquidada = false;

    // Renumera as parcelas
    renumberParcelas(draft);
  };

  const preSubmitHandler = () => {
    setIsSubmitting(true);
    submit();
  };

  const tipoDescontoChangeHandler = (e) => {
    const newTipo = e.target.value;

    if (newTipo === "$") {
      if (descontoEmEscudos >= porReceberMaisDesconto) {
        setDesconto(porReceberMaisDesconto);
        setDescontoEmEscudos(porReceberMaisDesconto);
      } else {
        setDesconto(descontoEmEscudos);
      }
    } else {
      const maxPercentage = (porReceberMaisDesconto / valorBase) * 100;
      const currentPercentage = (descontoEmEscudos / valorBase) * 100;

      if (currentPercentage >= maxPercentage) {
        setDesconto(maxPercentage);
        setDescontoEmEscudos(porReceberMaisDesconto);
      } else {
        setDesconto(currentPercentage);
        // Assegurar que descontoEmEscudos é um valor inteiro ao mudar para percentagem
        const roundedDescontoEmEscudos = Math.round(descontoEmEscudos);
        setDescontoEmEscudos(roundedDescontoEmEscudos);
      }
    }
    setTipoDesconto(newTipo);
  };

  const descontoChangeHandler = (e) => {
    const newValue = e.target.value;

    // Expressão regular para validar números com até um separador decimal
    const validNumberRegex = /^(\d*[.,]?\d*)$/;
    if (!validNumberRegex.test(newValue)) {
      return; // Ignora entradas inválidas
    }

    // Conta o número de pontos e vírgulas
    const decimalSeparators = (newValue.match(/[.,]/g) || []).length;

    // Se há mais de um separador decimal, mantenha o valor anterior
    if (decimalSeparators > 1) {
      return;
    }

    // Verifica se é tipo "$" e contém decimal
    if (tipoDesconto === "$") {
      // Rejeita qualquer input com decimal quando o tipo é "$"
      if (newValue.includes(".") || newValue.includes(",")) {
        return; // Não atualiza o estado, rejeitando a entrada
      }
    }

    // Substitui vírgula por ponto para cálculos internos
    const numericValue = newValue.replace(",", ".");

    // Se o valor não for um número válido, mantenha o valor anterior
    if (isNaN(parseFloat(numericValue)) && newValue !== "") {
      return;
    }

    // Processamento normal a partir daqui
    if (tipoDesconto === "$") {
      if (numericValue >= porReceberMaisDesconto) {
        setDesconto(porReceberMaisDesconto);
        setDescontoEmEscudos(porReceberMaisDesconto);
      } else {
        setDescontoEmEscudos(parseFloat(numericValue) || 0);
        setDesconto(newValue);
      }
    } else {
      const maxPercentage = (porReceberMaisDesconto / valorBase) * 100;

      if (numericValue >= maxPercentage) {
        setDesconto(maxPercentage);
        setDescontoEmEscudos(porReceberMaisDesconto);
      } else {
        // Para percentual, continua arredondando o valor em escudos
        const newDescontoEmEscudos = Math.round(
          (valorBase * numericValue) / 100,
        );
        setDescontoEmEscudos(newDescontoEmEscudos);
        setDesconto(newValue);
      }
    }
  };

  // Modificada para usar a nova abordagem
  function encontrarCombinacoesExatas(parcelas, valorAlvo) {
    const combinacoes = [];
    encontrarCombinacoesRecursivo(parcelas, valorAlvo, [], 0, 0, combinacoes);
    return combinacoes;
  }

  function encontrarCombinacoesRecursivo(
    parcelas,
    valorAlvo,
    combinacaoAtual,
    somaAtual,
    index,
    combinacoes,
  ) {
    if (Math.abs(somaAtual - valorAlvo) < 0.01) {
      combinacoes.push([...combinacaoAtual]);
      return;
    }

    if (index === parcelas.length || somaAtual > valorAlvo) {
      return;
    }

    // Incluir a parcela atual
    encontrarCombinacoesRecursivo(
      parcelas,
      valorAlvo,
      [...combinacaoAtual, parcelas[index]],
      somaAtual + parcelas[index].quantiaPorPagar,
      index + 1,
      combinacoes,
    );

    // Não incluir a parcela atual
    encontrarCombinacoesRecursivo(
      parcelas,
      valorAlvo,
      combinacaoAtual,
      somaAtual,
      index + 1,
      combinacoes,
    );
  }

  // Versão simplificada para encontrar a melhor combinação
  function encontrarMelhorCombinacao(parcelas, valorAlvo) {
    // Ordenar parcelas do menor para o maior valor
    const parcelasOrdenadas = [...parcelas].sort(
      (a, b) => a.quantiaPorPagar - b.quantiaPorPagar,
    );

    let melhorCombinacao = [];
    let melhorDiferenca = valorAlvo;

    // Tenta combinações simples primeiro (1 ou 2 parcelas)
    for (let i = 0; i < parcelasOrdenadas.length; i++) {
      const parcelaA = parcelasOrdenadas[i];
      const diferencaA = Math.abs(valorAlvo - parcelaA.quantiaPorPagar);

      if (diferencaA < melhorDiferenca) {
        melhorCombinacao = [parcelaA];
        melhorDiferenca = diferencaA;
      }

      // Tenta com duas parcelas
      for (let j = i + 1; j < parcelasOrdenadas.length; j++) {
        const parcelaB = parcelasOrdenadas[j];
        const soma = parcelaA.quantiaPorPagar + parcelaB.quantiaPorPagar;
        const diferenca = Math.abs(valorAlvo - soma);

        if (diferenca < melhorDiferenca) {
          melhorCombinacao = [parcelaA, parcelaB];
          melhorDiferenca = diferenca;
        }
      }
    }

    // Se a melhor combinação não for próxima o suficiente, tenta a abordagem gulosa
    if (melhorDiferenca > valorAlvo * 0.1 && parcelasOrdenadas.length > 2) {
      const combinacaoGulosa = [];
      let somaGulosa = 0;

      for (const parcela of parcelasOrdenadas) {
        if (somaGulosa + parcela.quantiaPorPagar <= valorAlvo) {
          combinacaoGulosa.push(parcela);
          somaGulosa += parcela.quantiaPorPagar;
        }
      }

      const diferencaGulosa = Math.abs(valorAlvo - somaGulosa);
      if (diferencaGulosa < melhorDiferenca) {
        melhorCombinacao = combinacaoGulosa;
      }
    }

    return [melhorCombinacao];
  }

  //Funcao para rearanjar a numeracao das parcelas ignorando a parcela de entrada caso ela exista

  const renumberParcelas = (contaFiltrada) => {
    let regularParcelaCount = 0;
    const regularParcelas = contaFiltrada.parcelas.filter((p) => !p.entrada);

    contaFiltrada.parcelas.forEach((p) => {
      if (!p.entrada) {
        regularParcelaCount++;
        p.nrParcela = `${regularParcelaCount}/${regularParcelas.length}`;
      } else {
        delete p.nrParcela; // Ensure entrada parcels don't have nrParcela
      }
    });

    contaFiltrada.nrParcelas = regularParcelas.length;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModalEditarDesconto}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className="fluxo-atendimento__modal__box aprovar-orcamento__modal"
          id="modal__cinquentaPorCento"
        >
          <h1 className="fluxo-atendimento__modal__titulo">
            Editar Desconto do Orçamento {isReembolso ? " de Reembolso " : ""}#
            {modalData.orcamento.codigo}
          </h1>

          {/* A isenção não tem botão próprio — faz-se por aqui, com 100%. Quem
              não souber disso procura uma opção que não existe e acaba a
              perdoar a dívida à mão, parcela a parcela.

              Quando há fatura emitida, este conselho deixa de servir: a
              isenção apagaria a parcela faturada. Por isso a nota troca, em
              vez de ficar a convidar para o caminho que vai ser recusado. */}
          {comFte.length > 0 ? (
            <NotaInformativa
              titulo="Esta conta já tem fatura eletrónica"
              style={{ margin: "0 0 18px" }}
            >
              {comFte.length === 1 ? "A parcela" : "As parcelas"}{" "}
              <strong>{comFte.map((p) => p.nrParcela || "?").join(", ")}</strong>{" "}
              já {comFte.length === 1 ? "foi faturada" : "foram faturadas"} e
              não {comFte.length === 1 ? "pode" : "podem"} mudar de valor.
              <br />
              Pode <strong>descontar até{" "}
              {modalData.parcelas
                .filter((p) => !p.parcelaPaga && !p.documentoEletronico)
                .reduce((s, p) => s + p.quantiaPorPagar, 0)
                .toLocaleString("pt-PT")}
              $00</strong>, que é o que sobra nas parcelas ainda livres. Para
              isentar por inteiro, anule primeiro a fatura com uma nota de
              crédito.
            </NotaInformativa>
          ) : (
            <NotaInformativa style={{ margin: "0 0 18px" }}>
              Para <strong>isentar o paciente do pagamento</strong>, dê um
              desconto de <strong>100%</strong> — ou do valor total em escudos.
              A conta fecha e fica marcada como isenta.
            </NotaInformativa>
          )}

          <div className="modalEditarDesconto__container">
            <FormControl className="relatorio__filtros__container__selectContainer">
              <InputLabel id="demo-simple-select-label">Tipo</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={tipoDesconto}
                label="Periodo"
                onChange={tipoDescontoChangeHandler}
              >
                <MenuItem value="%">%</MenuItem>
                <MenuItem value="$">$</MenuItem>
              </Select>
            </FormControl>

            <TextField
              className="modalEditarDesconto__container__inputText"
              label="Desconto"
              value={desconto}
              onChange={descontoChangeHandler}
            />

            <span className="modalEditarDesconto__container__valor">
              Valor: {descontoEmEscudos.toLocaleString("pt-br")}$00
            </span>
          </div>

          <div className="modalEditarDesconto__dadosOrcamento">
            <span className="modalEditarDesconto__dadosOrcamento__titulo">
              Dados do Orçamento
            </span>
            <span className="modalEditarDesconto__dadosOrcamento__subtitulo">
              Antes
            </span>
            <span className="modalEditarDesconto__dadosOrcamento__subtitulo">
              Depois
            </span>

            {/* Subtotal antes */}
            <div className="modalEditarDesconto__dadosOrcamento__linha">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Subtotal
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {modalData.orcamento.subtotal.toLocaleString("pt-Br")}
              </span>
            </div>

            {/* Subtotal depois */}
            <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Subtotal
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {modalData.orcamento.subtotal.toLocaleString("pt-Br")}
              </span>
            </div>

            {isReembolso && (
              <>
                {/* Subtotal antes */}
                <div className="modalEditarDesconto__dadosOrcamento__linha">
                  <span className="modalEditarDesconto__dadosOrcamento__legenda">
                    Subtotal Cliente
                  </span>
                  <span className="modalEditarDesconto__dadosOrcamento__valor">
                    {modalData.orcamento.subtotalCliente.toLocaleString(
                      "pt-Br",
                    )}
                  </span>
                </div>

                {/* Subtotal depois */}
                <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
                  <span className="modalEditarDesconto__dadosOrcamento__legenda">
                    Subtotal Cliente
                  </span>
                  <span className="modalEditarDesconto__dadosOrcamento__valor">
                    {modalData.orcamento.subtotalCliente.toLocaleString(
                      "pt-Br",
                    )}
                  </span>
                </div>
              </>
            )}

            {/* Majoração antes */}
            {modalData.orcamento.comMajoracao && (
              <div className="modalEditarDesconto__dadosOrcamento__linha">
                <span className="modalEditarDesconto__dadosOrcamento__legenda">
                  Majoração
                </span>
                <span className="modalEditarDesconto__dadosOrcamento__valor">
                  {modalData.orcamento.majoracao.toLocaleString("pt-Br")}
                </span>
              </div>
            )}

            {/* Majoração depois */}
            {modalData.orcamento.comMajoracao && (
              <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
                <span className="modalEditarDesconto__dadosOrcamento__legenda">
                  Majoração
                </span>
                <span className="modalEditarDesconto__dadosOrcamento__valor">
                  {modalData.orcamento.majoracao.toLocaleString("pt-Br")}
                </span>
              </div>
            )}

            {/* Desconto antes */}
            <div className="modalEditarDesconto__dadosOrcamento__linha">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Desconto
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {modalData.orcamento.desconto.toLocaleString("pt-Br")}
              </span>
            </div>

            {/* Desconto depois */}
            {/* /////////////////////////////////////////////////////////////////////////////////////////// */}
            <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Desconto
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {descontoEmEscudos.toLocaleString("pt-Br")}
              </span>
            </div>

            {/* Total antes */}
            <div className="modalEditarDesconto__dadosOrcamento__linha">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Total
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {modalData.orcamento.total.toLocaleString("pt-Br")}
              </span>
            </div>
            {/* Total depois */}
            <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Total
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {calcularTotal(descontoEmEscudos).toLocaleString("pt-Br")}
              </span>
            </div>

            <div className="modalEditarDesconto__dadosOrcamento__linha">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Por Receber
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {porReceber.toLocaleString("pt-Br")}
              </span>
            </div>

            <div className="modalEditarDesconto__dadosOrcamento__linha modalEditarDesconto__dadosOrcamento__linha--right">
              <span className="modalEditarDesconto__dadosOrcamento__legenda">
                Por Receber
              </span>
              <span className="modalEditarDesconto__dadosOrcamento__valor">
                {(
                  porReceber +
                  modalData.orcamento.desconto -
                  descontoEmEscudos
                ).toLocaleString("pt-Br")}
              </span>
            </div>
          </div>

          <div className="fluxo-atendimento__modal__bottom_btns">
            <span
              className="cancel-btn"
              onClick={handleCloseModalEditarDesconto}
              style={{ display: "block" }}
            >
              Fechar
            </span>

            <span
              className="blue-button"
              disabled={isSubmitting}
              onClick={preSubmitHandler}
            >
              {isSubmitting ? "Guardando..." : "Guardar"}
            </span>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default ModalEditarDesconto;
